import React, { useEffect, useState } from "react";
import { 
  Phone, Clock, Play, Search, Filter, Download, 
  CheckCircle, AlertTriangle, User, Calendar, ChevronDown
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEMO_API_KEY = "test_key_123";

export const CallsPage = () => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOutcome, setFilterOutcome] = useState("all");
  const [expandedCall, setExpandedCall] = useState(null);

  useEffect(() => {
    const loadCalls = async () => {
      try {
        const res = await fetch(`${API_URL}/api/calls?limit=50`, {
          headers: { "X-API-Key": DEMO_API_KEY }
        });
        if (res.ok) {
          const data = await res.json();
          setCalls(data.calls || []);
        }
      } catch (err) {
        console.error("Failed to load calls:", err);
      } finally {
        setLoading(false);
      }
    };
    loadCalls();
  }, []);

  // Demo data if no real calls
  const displayCalls = calls.length > 0 ? calls : [
    { id: "1", caller_number: "+1 (555) 123-4567", caller_name: "John Smith", duration_seconds: 180, outcome: "booked", created_at: new Date(Date.now() - 1000*60*5).toISOString(), summary: "Customer called to book a dental cleaning appointment for next Tuesday at 2pm. Confirmed availability and scheduled." },
    { id: "2", caller_number: "+1 (555) 234-5678", caller_name: "Sarah Johnson", duration_seconds: 95, outcome: "lead", created_at: new Date(Date.now() - 1000*60*30).toISOString(), summary: "Inquiry about teeth whitening services. Requested pricing information and asked to be called back tomorrow." },
    { id: "3", caller_number: "+1 (555) 345-6789", caller_name: "Mike Wilson", duration_seconds: 240, outcome: "booked", created_at: new Date(Date.now() - 1000*60*60).toISOString(), summary: "Emergency call - patient experiencing tooth pain. Scheduled same-day appointment for 4:30pm." },
    { id: "4", caller_number: "+1 (555) 456-7890", caller_name: "Emily Davis", duration_seconds: 60, outcome: "info", created_at: new Date(Date.now() - 1000*60*120).toISOString(), summary: "Called to confirm business hours and location. Provided directions and parking information." },
    { id: "5", caller_number: "+1 (555) 567-8901", caller_name: "Robert Brown", duration_seconds: 320, outcome: "booked", created_at: new Date(Date.now() - 1000*60*180).toISOString(), summary: "New patient consultation booking. Discussed services, insurance acceptance, and scheduled initial exam for Friday." },
  ];

  // Filter calls
  const filteredCalls = displayCalls.filter(call => {
    const matchesSearch = !searchTerm || 
      call.caller_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      call.caller_number?.includes(searchTerm);
    const matchesFilter = filterOutcome === "all" || call.outcome === filterOutcome;
    return matchesSearch && matchesFilter;
  });

  // Stats
  const totalCalls = displayCalls.length;
  const totalMinutes = Math.round(displayCalls.reduce((sum, c) => sum + (c.duration_seconds || 0), 0) / 60);
  const bookedCalls = displayCalls.filter(c => c.outcome === "booked").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Call History</h1>
          <p className="text-slate-500 mt-1">Review all calls handled by your AI receptionist</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors">
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Phone className="w-6 h-6" />
            </div>
            <div>
              <p className="text-blue-100 text-sm">Total Calls</p>
              <p className="text-3xl font-bold">{totalCalls}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-5 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <p className="text-purple-100 text-sm">Total Minutes</p>
              <p className="text-3xl font-bold">{totalMinutes}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-5 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Calendar className="w-6 h-6" />
            </div>
            <div>
              <p className="text-emerald-100 text-sm">Bookings Made</p>
              <p className="text-3xl font-bold">{bookedCalls}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name or phone number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="relative">
          <select
            value={filterOutcome}
            onChange={(e) => setFilterOutcome(e.target.value)}
            className="appearance-none pl-4 pr-10 py-3 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
          >
            <option value="all">All Outcomes</option>
            <option value="booked">Booked</option>
            <option value="lead">Lead</option>
            <option value="info">Info</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {/* Calls List */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-slate-500 mt-4">Loading calls...</p>
          </div>
        ) : filteredCalls.length === 0 ? (
          <div className="p-8 text-center">
            <Phone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No calls found</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredCalls.map((call) => (
              <div 
                key={call.id}
                className="hover:bg-slate-50 transition-colors cursor-pointer"
                onClick={() => setExpandedCall(expandedCall === call.id ? null : call.id)}
              >
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      call.outcome === "booked" ? "bg-emerald-100 text-emerald-600" :
                      call.outcome === "lead" ? "bg-amber-100 text-amber-600" :
                      "bg-blue-100 text-blue-600"
                    }`}>
                      {call.outcome === "booked" ? <CheckCircle className="w-6 h-6" /> :
                       call.outcome === "lead" ? <User className="w-6 h-6" /> :
                       <Phone className="w-6 h-6" />}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{call.caller_name || "Unknown Caller"}</p>
                      <p className="text-sm text-slate-500">{call.caller_number}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-right hidden sm:block">
                      <p className="text-sm font-medium text-slate-700">
                        {formatDuration(call.duration_seconds)}
                      </p>
                      <p className="text-xs text-slate-400">
                        {formatTime(call.created_at)}
                      </p>
                    </div>
                    
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      call.outcome === "booked" ? "bg-emerald-100 text-emerald-700" :
                      call.outcome === "lead" ? "bg-amber-100 text-amber-700" :
                      "bg-blue-100 text-blue-700"
                    }`}>
                      {call.outcome?.charAt(0).toUpperCase() + call.outcome?.slice(1) || "Info"}
                    </span>

                    {call.recording_url && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); window.open(call.recording_url, '_blank'); }}
                        className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                      >
                        <Play className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedCall === call.id && (
                  <div className="px-4 pb-4 pt-0">
                    <div className="bg-slate-50 rounded-xl p-4 ml-16">
                      <p className="text-sm font-medium text-slate-700 mb-2">Call Summary</p>
                      <p className="text-sm text-slate-600">{call.summary || "No summary available."}</p>
                      
                      <div className="mt-4 flex flex-wrap gap-4 text-sm">
                        <div>
                          <span className="text-slate-400">Duration:</span>{" "}
                          <span className="text-slate-700 font-medium">{formatDuration(call.duration_seconds)}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Time:</span>{" "}
                          <span className="text-slate-700 font-medium">{new Date(call.created_at).toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Outcome:</span>{" "}
                          <span className="text-slate-700 font-medium capitalize">{call.outcome}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

function formatDuration(seconds) {
  if (!seconds) return "0s";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
}

function formatTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export default CallsPage;
